'use client'
import { useScrollElementContext } from "@/provider/ScrollElementProvider"
import { ChevronDown } from "@/ui/icons/ChevronDown"
import { Button, cn } from "@heroui/react"
import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useState } from "react"

type Props = {
  // 'bottom'：column-reverse 容器(主聊天)，点击回到最新消息(视觉底部)，箭头朝下；
  // 'top'  ：普通向下滚动容器(首页/搜索页)，点击回到顶部，箭头朝上。
  mode?: 'top' | 'bottom'
  threshold?: number
  className?: string
}

// 滚动到边缘(顶部/底部)的浮动按钮。
// 复用 ScrollElementProvider 提供的滚动容器：
// - 普通容器视觉顶部 scrollTop≈0；column-reverse 容器视觉底部 scrollTop≈0。
// - 两种情况都是“离开 scrollTop=0 的那一端超过阈值时显示，点击平滑回到 top:0”，
//   因此用 |scrollTop| > threshold 统一判断、scrollTo({ top: 0 }) 统一回弹。
export const ScrollEdgeButton = ({ mode = 'top', threshold = 300, className }: Props) => {
  const { container } = useScrollElementContext()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = container.current
    if (!el) return

    const update = () => setVisible(Math.abs(el.scrollTop) > threshold)

    update()
    el.addEventListener('scroll', update, { passive: true })
    return () => el.removeEventListener('scroll', update)
  }, [container, threshold])

  const onPress = () => {
    container.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className={cn("absolute bottom-4 right-4 z-20", className)}
          initial={{ opacity: 0, scale: 0.8, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 8 }}
          transition={{ duration: 0.15 }}
        >
          <Button
            isIconOnly
            radius="full"
            aria-label={mode === 'bottom' ? "Scroll to latest message" : "Scroll to top"}
            onPress={onPress}
            className={cn(
              "h-10 w-10 min-w-0",
              "bg-light-gray border-1 border-theme-primary/5 shadow-lg",
              "text-purple-blue backdrop-blur-md hover:opacity-90",
            )}
          >
            <ChevronDown width={22} height={22} className={cn(mode === 'top' && "rotate-180")} />
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
